/* DATORACODE - UGC Story-Modal
 *
 * Standalone-Story-Modal für UGC-Cards. Liest das normalisierte Card-Array aus dem
 * section-eigenen script[data-ugc-data] (Vertrag siehe snippets/ugc-story-modal.liquid)
 * und lädt das Produktpanel lazy + variant-aware als server-rendered Section
 * (sections/ugc-story-panel.liquid) — Preis/Datora/Verfügbarkeit bleiben damit
 * serverseitige Source of Truth, das JS patcht keine Preise.
 *
 * Forward-Compat: hält {cards, currentIndex} und kapselt den Video-Lifecycle
 * (mountVideo/playVideo/pauseVideo/resetVideo/releaseVideo) sowie goTo/next/prev
 * als Andockpunkte für Story-Pagination/Auto-Advance und Video-Preloading.
 */
(function () {
  if (customElements.get('ugc-story-modal')) return;

  /* Einmal konstruieren statt pro rAF-Frame (tick) — window.matchMedia kann fehlen (Guard). */
  const reducedMotionMql =
    typeof window.matchMedia === 'function' ? window.matchMedia('(prefers-reduced-motion: reduce)') : null;
  const prefersReducedMotion = () => !!(reducedMotionMql && reducedMotionMql.matches);

  const INTERACTIVE_SELECTOR = 'a, button, input, select, textarea, label, [role="button"], [data-ugc-ignore-modal]';

  /* Fallback-Dauer (ms) für Cards ohne abspielbares Video (image-only / Video-Fehler):
     Pagination-Füllung + Auto-Advance laufen dann timergesteuert. */
  const STORY_IMAGE_DURATION = 5000;

  /* Adapter auf die data-ugc-data-Form der UGC-Section: "video" ist dort ein URL-String
     (kann "" sein), "video_url" der Fallback. Poster-Priorität: "video_poster" (echtes
     Shopify-Video-preview_image, kein Blitzen fremder Bilder) → Objekt-Form video.poster →
     "image" (Card-/Social-Bild als letzter Fallback). Die Objekt-Form
     { sources: [{ url, mime }], poster } wird defensiv weiter unterstützt. */
  const normalizeVideo = (card) => {
    if (!card) return null;

    const sources = [];
    let poster = null;
    const raw = card.video;

    if (typeof card.video_poster === 'string' && card.video_poster !== '') {
      poster = card.video_poster;
    }

    if (raw && typeof raw === 'object') {
      (Array.isArray(raw.sources) ? raw.sources : []).forEach((entry) => {
        if (!entry || !entry.url) return;
        sources.push({ url: entry.url, mime: entry.mime || entry.mime_type || null });
      });
      if (!poster && typeof raw.poster === 'string' && raw.poster !== '') poster = raw.poster;
    } else if (typeof raw === 'string' && raw !== '') {
      sources.push({ url: raw, mime: null });
    }

    if (sources.length === 0 && typeof card.video_url === 'string' && card.video_url !== '') {
      sources.push({ url: card.video_url, mime: null });
    }

    if (!poster && typeof card.image === 'string' && card.image !== '') {
      poster = card.image;
    }

    return sources.length > 0 ? { sources, poster } : null;
  };

  class UgcStoryModal extends HTMLElement {
    constructor() {
      super();

      this.cards = [];
      this.currentIndex = -1;
      this.currentVideo = null;
      this.muted = true;
      this.helper = null;
      this.panelAbort = null;
      this.pendingVariantId = null;
      this.pendingSubmitButton = null;
      /* activePicker = 'color' | 'size' | null (immer nur EINES offen); popoverOpen leitet sich ab.
         _resumeAfterPicker: war die Story beim Öffnen des Sheets aktiv (steuert Resume beim Schließen).
         _pendingPickerFetch: Token des laufenden Picker-getriebenen Panel-Re-Fetch — solange gesetzt, besitzt
         dessen Settlement das Resume (Reopen→Close ohne Auswahl darf nicht vorzeitig fortsetzen). */
      this.activePicker = null;
      this._resumeAfterPicker = false;
      this._pendingPickerFetch = null;
      /* _panelFetchToken: Stale-Panel-Interlock — gesetzt solange ein loadPanel-Fetch läuft, gelöscht NUR in
         dessen finally (token-match) bzw. in goTo/handleClose-Teardown. Getrennt von _pendingPickerFetch (Resume),
         das togglePlay löscht — manueller Play/Pause darf den Interlock NICHT aufheben. */
      this._panelFetchToken = null;
      this.mobileMql = window.matchMedia(
        '(max-width: 749.98px), (min-width: 750px) and (max-width: 990.98px) and (min-height: 700px)'
      );

      /* Story-Pagination / Auto-Advance */
      this.endBehavior = 'stop';
      this._progressRaf = null;
      this._progressElapsed = 0;
      this._progressLastTs = 0;
      this._forceTimer = false;
      this._autoplayFallback = false;
      this._paginationBuilt = false;
      this._switching = false;
      this._activeFill = null;

      this.onDocumentClick = this.onDocumentClick.bind(this);
      this.syncPlayUI = this.syncPlayUI.bind(this);
      this.tick = this.tick.bind(this);
      this.onVideoPlay = this.onVideoPlay.bind(this);
      this.onVideoPause = this.onVideoPause.bind(this);
      this.onVideoEnded = this.onVideoEnded.bind(this);
      this.onVideoError = this.onVideoError.bind(this);

      /* Element-scoped Delegation ist im Constructor sicher (braucht keine Kinder) */
      this.addEventListener('click', (event) => {
        const pickerTrigger = event.target.closest('[data-ugc-trigger]');
        if (pickerTrigger) {
          const picker = pickerTrigger.closest('[data-ugc-picker]');
          const key = picker && picker.getAttribute('data-ugc-picker');
          if (key && this.activePicker === key) this.closePopover();
          else if (key) this.openPopover(key);
          return;
        }
        if (event.target.closest('[data-ugc-popover-close]')) {
          this.closePopover();
          return;
        }
        const tapZone = event.target.closest('[data-ugc-tap]');
        if (tapZone) {
          if (tapZone.dataset.ugcTap === 'prev') this.prev();
          else this.next();
          return;
        }
        const prevBtn = event.target.closest('[data-ugc-prev]');
        if (prevBtn) {
          /* aria-disabled (Boundary im stop-Modus): kein prev — sichtbar/fokussierbar, aber no-op */
          if (prevBtn.getAttribute('aria-disabled') !== 'true') this.prev();
          return;
        }
        const nextBtn = event.target.closest('[data-ugc-next]');
        if (nextBtn) {
          if (nextBtn.getAttribute('aria-disabled') !== 'true') this.next();
          return;
        }
        if (event.target.closest('[data-ugc-mute]')) {
          this.toggleMute();
          return;
        }
        if (event.target.closest('[data-ugc-play]')) this.togglePlay();
      });

      /* Capture-Phase: Outside-Klick schließt ERST den Popover — muss vor dem
         Overlay-Target-Listener des ModalHelpers greifen (modal-helpers.js:76). */
      this.addEventListener(
        'click',
        (event) => {
          if (!this.popoverOpen) return;
          if (event.target.closest('[data-ugc-popover], [data-ugc-trigger]')) return;
          event.preventDefault();
          event.stopPropagation();
          this.closePopover();
        },
        true
      );

      /* ESC schließt erst den Popover, nicht die Modal: ModalHelper hört auf document
         (modal-helpers.js:77), Element-Bubble kommt davor dran. */
      this.addEventListener('keydown', (event) => {
        if (!this.popoverOpen) return;
        if (event.key === 'Escape') {
          event.stopPropagation();
          this.closePopover();
          return;
        }
        /* Tab zyklisch im Sheet halten: die enabled Radios (native Radiogroup = EIN Tab-Stopp) PLUS der
           sichtbare Close-Button — ohne Abfangen fiele der Fokus hinter das Sheet (CTA/Links) durch, obwohl
           der Trap-Container der Popover ist (Dawn-Trap fängt nur first/last). Der Close-Button MUSS im
           Zyklus sein, sonst ist er per Tastatur unerreichbar (sonst schließen nur Esc/Outside-Click). */
        if (event.key === 'Tab') {
          const els = this._pickerEls(this.activePicker);
          const popover = els && els.popover;
          if (!popover || !popover.contains(document.activeElement)) return;
          const focusables = Array.from(
            popover.querySelectorAll('[data-ugc-popover-close]:not(:disabled), input[type="radio"]:not(:disabled)')
          );
          if (focusables.length === 0) return;
          event.preventDefault();
          const index = focusables.indexOf(document.activeElement);
          const next = event.shiftKey
            ? focusables[(index - 1 + focusables.length) % focusables.length]
            : focusables[(index + 1) % focusables.length];
          next.focus();
        }
      });

      /* Pfeiltasten = Story prev/next; nicht bei offenem Popover oder Fokus auf einem
         Panel-Control (native Radiogroup nutzt Pfeile für die Größenwahl). */
      this.addEventListener('keydown', (event) => {
        if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
        if (!this.helper || !this.helper.isOpen() || this.popoverOpen) return;
        const target = event.target;
        if (
          target &&
          (target.matches('input, select, textarea, [contenteditable]') ||
            target.closest('[data-ugc-panel-target]'))
        )
          return;
        event.preventDefault();
        if (event.key === 'ArrowLeft') this.prev();
        else this.next();
      });

      this.addEventListener('change', (event) => {
        const input = event.target;
        if (!input.matches('[data-ugc-panel-slot] input[type="radio"][data-variant-id]')) return;
        /* Läuft bereits ein Panel-Fetch (_panelFetchToken gesetzt), diese (schnelle zweite) Auswahl IGNORIEREN:
           das alte Panel bleibt während des Fetch sichtbar — ein zweiter Wechsel würde sonst eine stale
           data-variant-id daraus lesen. Interlock ist _panelFetchToken (NICHT _pendingPickerFetch): er überlebt
           togglePlay, das nur den Resume-Token löscht. Greift für Pointer UND Tastatur (change feuert für beide);
           KEIN disabled/Strike an den Optionen (das ließe alle Swatches als ausverkauft erscheinen). Nach dem
           Settle re-rendert das Panel die tatsächlich aufgelöste Auswahl. */
        if (this._panelFetchToken) return;
        const card = this.cards[this.currentIndex];
        if (!card || !card.product) return;
        /* Variant-Id VOR closePopover sichern — der Sync setzt die Radios auf disabled.
           Mit Fokus-Rückgabe schließen: re-armt den Trap auf den Trigger für das
           Lade-Fenster (Slow-3G) — sonst hängt der Fokus auf dem soeben disabled
           gewordenen Radio/body, während die Trap-Grenzen noch vom offenen Popover
           gecacht sind (global.js trapFocus cached first/last). */
        const variantId = input.dataset.variantId;
        const pickerEl = input.closest('[data-ugc-picker]');
        const focusPicker = pickerEl && pickerEl.getAttribute('data-ugc-picker');
        /* resume:false + Fetch-Token: die Story NICHT sofort fortsetzen — sie ruht, bis der NICHT abgebrochene
           loadPanel gesettled ist. Der Token bindet das Resume an GENAU diesen Fetch: ein neuerer Wechsel
           ersetzt ihn (alte/abgebrochene Fetches setzen nicht fort), und ein Reopen→Close ohne Auswahl in der
           Zwischenzeit lässt _maybeResumeAfterPicker den Intent armiert (Token gesetzt → kein vorzeitiges Resume,
           sonst liefe die Story mitten im Fetch und könnte weiterschalten + ihn abbrechen). */
        const token = {};
        this._pendingPickerFetch = token;
        this.closePopover({ resume: false });
        this.loadPanel(card, { variantId: variantId, focusVariantId: variantId, focusPicker: focusPicker, announcePrice: true }).then(
          (settled) => {
            if (this._pendingPickerFetch !== token) return;
            this._pendingPickerFetch = null;
            if (settled) this._maybeResumeAfterPicker();
          }
        );
      });

      /* Slow-Refetch-Lock: solange _panelFetchToken gesetzt ist (Variant-Re-Fetch in flight, altes Panel noch
         sichtbar), Picker-Aktivierung in der Capture-Phase VOR dem nativen Radio-Toggle abfangen, damit eine
         (vom change-Handler ohnehin ignorierte) zweite Auswahl nicht visuell bestätigt. pointerdown deckt
         Maus/Touch/Pen ab, click den Label→Radio-Toggle. Kein disabled/Strike, kein Queue, CTA/id unberührt. */
      const blockPickerActivation = (event) => {
        if (!this._panelFetchToken) return;
        const target = event.target;
        if (!target || !target.closest) return;
        if (!target.closest('[data-ugc-picker] input[type="radio"], [data-ugc-picker] label, [data-ugc-trigger]')) return;
        event.preventDefault();
        event.stopPropagation();
      };
      this.addEventListener('pointerdown', blockPickerActivation, true);
      this.addEventListener('click', blockPickerActivation, true);

      /* Submit der Panel-Form merken — nur dieser darf das Modal später via cartUpdate schließen.
         Disabled/leerer Variant-Id (z.B. durch lockPanel) setzt KEIN Pending. */
      this.addEventListener('submit', (event) => {
        const form = event.target;
        if (!form || !form.closest('[data-ugc-panel-target]')) return;
        const idInput = form.querySelector('input[name="id"]');
        const value = idInput && !idInput.disabled ? String(idInput.value || '') : '';
        this.pendingVariantId = value || null;
        /* CTA merken, um „Submit in flight" am .loading-Zustand statt nur am Cart-Event abzulesen:
           ein Netzwerk-Fehler publiziert weder cartError noch cartUpdate, aber product-form.js entfernt
           .loading IMMER (finally) — siehe _isSubmitPending(). */
        this.pendingSubmitButton = value ? form.querySelector('[data-ugc-cta]') : null;
      });
    }

    /* popoverOpen leitet sich aus activePicker ab — jede bestehende Leseseite bleibt unverändert. */
    get popoverOpen() {
      return this.activePicker !== null;
    }

    connectedCallback() {
      /* CFSN-622: Im main-product-Block sitzt das Modal in der Produktinfo-Spalte, deren Vorfahr
         einen transform/containment setzt — dort verankert der Browser position:fixed am Vorfahren
         statt am Viewport, das Modal bliebe in der schmalen Spalte gefangen. Deshalb im Block-Kontext
         ans <body> portalen. Der Daten-Wrapper wird VOR dem Move gemerkt (danach findet closest() ihn
         nicht mehr). Das appendChild löst synchron disconnect→connect aus; _relocating überspringt beides,
         damit weder doppelt initialisiert noch das Setup wieder abgeräumt wird. Section-Instanzen (direktes
         Kind des Section-Roots) bleiben unangetastet. */
      if (this._relocating) this._relocating = false;

      if (!this._portaled) {
        this._portaled = true;
        this.wrapper = this.closest('[data-ugc-section-id]');
        const inBlock = this.wrapper && this.wrapper.classList.contains('ugc-section--block');
        if (inBlock && this.parentElement !== document.body) {
          /* Vor dem Umhängen etwaige verwaiste Body-Instanzen gleicher ID entfernen (Theme-Editor
             re-rendert die Section, unser Body-Modal läge sonst außerhalb und bliebe stehen). */
          if (this.id) {
            document.querySelectorAll('#' + CSS.escape(this.id)).forEach((el) => {
              if (el !== this && el.parentElement === document.body) el.remove();
            });
          }
          this._relocating = true;
          document.body.appendChild(this);
          return;
        }
      }
      if (!this.wrapper) this.wrapper = this.closest('[data-ugc-section-id]');

      this.video = this.querySelector('[data-ugc-video]');
      this.prebufferVideo = this.querySelector('[data-ugc-prebuffer]');
      this.stageImage = this.querySelector('[data-ugc-image]');
      this.panelSlot = this.querySelector('[data-ugc-panel-slot]');
      this.panelTarget = this.querySelector('[data-ugc-panel-target]');
      this.muteButton = this.querySelector('[data-ugc-mute]');
      this.playButton = this.querySelector('[data-ugc-play]');
      this.pagination = this.querySelector('[data-ugc-pagination]');
      this.progressStatus = this.querySelector('[data-ugc-progress-status]');
      this.priceStatus = this.querySelector('[data-ugc-price-status]');
      this.prevButton = this.querySelector('[data-ugc-prev]');
      this.nextButton = this.querySelector('[data-ugc-next]');
      this.tapPrev = this.querySelector('[data-ugc-tap="prev"]');
      this.tapNext = this.querySelector('[data-ugc-tap="next"]');
      this.cards = this.readCards();
      this.endBehavior = this.dataset.ugcEndBehavior === 'loop' ? 'loop' : 'stop';

      if (this.video) {
        this.video.addEventListener('play', this.onVideoPlay);
        this.video.addEventListener('pause', this.onVideoPause);
        this.video.addEventListener('ended', this.onVideoEnded);
        this.video.addEventListener('error', this.onVideoError);
      }

      /* Viewport-Wechsel: mobile→desktop muss Pills wieder bedienbar machen (temp-disabled
         aufräumen), desktop→mobile stellt den kollabierten Default her. */
      this.onMqlChange = (event) => {
        this.syncTriggers();
        if (!event.matches) {
          /* → Desktop: offenes Sheet auflösen (egal welcher Picker), ggf. pausierte Story fortsetzen,
             Pills inline wieder bedienbar machen, Trap ohne Fokusklau neu rechnen. */
          if (this.activePicker) {
            const els = this._pickerEls(this.activePicker);
            if (els && els.popover) els.popover.classList.remove('is-open');
            if (els && els.trigger) els.trigger.setAttribute('aria-expanded', 'false');
            this.activePicker = null;
            this._maybeResumeAfterPicker();
          }
          this.syncPopoverRadios(false);
          if (this.helper && this.helper.isOpen()) this.rearmTrap(null);
        } else if (this.helper && this.helper.isOpen()) {
          /* → Mobile: alle Sheets kollabieren (keines offen), Trap auf ein stabiles Ziel verankern. */
          this.syncPopoverRadios(true);
          this.rearmTrap(this._stableTrapTarget());
        }
      };
      this.mobileMql.addEventListener('change', this.onMqlChange);

      if (this.panelSlot && 'ResizeObserver' in window) {
        /* Mobile: hält das Meta-Overlay über dem Bottom-Sheet (CSS nutzt --ugc-sheet-height) */
        this.panelResizeObserver = new ResizeObserver(() => {
          this.style.setProperty('--ugc-sheet-height', this.panelSlot.offsetHeight + 'px');
        });
        this.panelResizeObserver.observe(this.panelSlot);
      }

      document.addEventListener('click', this.onDocumentClick);

      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        /* Cart-Drawer (z-index 1000) liegt unter dem Modal (9999) → nur nach erfolgreichem
           Add-to-Cart AUS DIESEM PANEL schließen: source UND Variant-Id müssen zum gemerkten
           Submit passen. Datora publiziert cartUpdate { source: 'datora' } bei Cart-Refreshes
           (datora.js) — das darf die Story nicht schließen. Fehler schließen ebenfalls NICHT
           (product-form zeigt sie im Error-Wrapper des Panels). */
        this.unsubscribeCartUpdate = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
          if (!this.helper || !this.helper.isOpen()) return;
          if (!event || event.source !== 'product-form') return;
          if (!this.pendingVariantId || String(event.productVariantId) !== this.pendingVariantId) return;
          this.pendingVariantId = null;
          this.pendingSubmitButton = null;
          this.helper.close();
        });
        this.unsubscribeCartError = subscribe(PUB_SUB_EVENTS.cartError, () => {
          this.pendingVariantId = null;
          this.pendingSubmitButton = null;
        });
      }
    }

    disconnectedCallback() {
      /* CFSN-622: transienter disconnect während des Body-Portals (siehe connectedCallback) — nichts abräumen. */
      if (this._relocating) return;
      document.removeEventListener('click', this.onDocumentClick);
      this.stopProgress();
      if (this.video) {
        this.video.removeEventListener('play', this.onVideoPlay);
        this.video.removeEventListener('pause', this.onVideoPause);
        this.video.removeEventListener('ended', this.onVideoEnded);
        this.video.removeEventListener('error', this.onVideoError);
      }
      if (this.panelResizeObserver) {
        this.panelResizeObserver.disconnect();
        this.panelResizeObserver = null;
      }
      if (this.onMqlChange) {
        this.mobileMql.removeEventListener('change', this.onMqlChange);
        this.onMqlChange = null;
      }
      if (this.unsubscribeCartUpdate) {
        this.unsubscribeCartUpdate();
        this.unsubscribeCartUpdate = null;
      }
      if (this.unsubscribeCartError) {
        this.unsubscribeCartError();
        this.unsubscribeCartError = null;
      }
      if (this.panelAbort) {
        this.panelAbort.abort();
        this.panelAbort = null;
      }
      /* Offene Modal beim Entfernen schließen — close() gibt Scroll-Lock + Focus-Trap frei und läuft über
         onClose→handleClose (releaseVideo/clearPanel). Sonst (nicht offen / kein helper) direkt releaseVideo. */
      if (this.helper && this.helper.isOpen()) {
        this.helper.close();
      } else {
        this.releaseVideo();
      }
      if (this.helper) {
        this.helper.destroy();
        this.helper = null;
      }
    }

    readCards() {
      const script = this.wrapper && this.wrapper.querySelector('script[data-ugc-data]');
      if (!script) {
        console.error('UGC story modal: script[data-ugc-data] fehlt im Section-Wrapper');
        return [];
      }
      try {
        const data = JSON.parse(script.textContent);
        if (!Array.isArray(data)) throw new Error('data-ugc-data ist kein Array');
        return data;
      } catch (error) {
        console.error('UGC story modal: data-ugc-data unlesbar', error);
        return [];
      }
    }

    onDocumentClick(event) {
      const opener = event.target.closest('[data-ugc-index]');
      if (!opener || !this.wrapper || !this.wrapper.contains(opener) || this.contains(opener)) return;

      /* Vertrag: alles mit [data-ugc-ignore-modal] öffnet nicht — auch der Opener selbst.
         Der Descendant-Guard unten greift nur bei interactive !== opener. */
      if (opener.hasAttribute('data-ugc-ignore-modal')) return;

      /* Interaktive Nachfahren der Card (zukünftige Links/Buttons) öffnen das Modal nicht */
      const interactive = event.target.closest(INTERACTIVE_SELECTOR);
      if (interactive && interactive !== opener && opener.contains(interactive)) return;

      /* Number('') wäre 0 — leerer/fehlender Index darf nicht still Card 0 öffnen */
      const raw = opener.dataset.ugcIndex;
      if (!raw || raw.trim() === '') return;
      const index = Number(raw);
      if (!Number.isInteger(index) || !this.cards[index]) return;

      /* preventDefault erst NACH ensureHelper — käme ein Klick vor dem Laden von
         modal-helpers.js, würde sonst die Default-Aktion des Openers geschluckt, ohne
         dass sich das Modal öffnet (ensureHelper ist idempotent). */
      this.ensureHelper();
      if (!this.helper) return;
      event.preventDefault();
      this.openAt(index, opener);
    }

    ensureHelper() {
      if (this.helper) return;
      if (typeof window.ModalHelper !== 'function') {
        /* modal-helpers.js wird in theme.liquid erst NACH content_for_layout geladen,
           deshalb lazy hier (beim ersten Open) statt im connectedCallback. */
        console.error('UGC story modal: ModalHelper nicht verfügbar');
        return;
      }
      this.helper = new window.ModalHelper(this, {
        overlaySelector: '[data-modal-overlay]',
        closeSelector: '[data-modal-close]',
        /* focusTarget ist der TRAP-CONTAINER für trapFocus — Content-Wrapper, kein Button */
        focusTarget: () => this.querySelector('[data-ugc-modal-content]') || this,
        onClose: () => this.handleClose(),
      });
    }

    /* ── Öffnen / Schließen / Navigation ───────────────────────────── */

    openAt(index, trigger = null) {
      const card = this.cards[index];
      if (!card) return;

      this.ensureHelper();
      if (!this.helper) return;

      if (typeof window.pauseAllMedia === 'function') window.pauseAllMedia();

      this.muted = true;
      this.buildPagination();
      this.showCard(index);
      this.helper.open({ restoreFocusTo: trigger });
      /* Nach helper.open (helper.isOpen() == true für playVideo/startProgress) */
      this.beginStory();

      this.dispatch('ugc-story:open');
    }

    close() {
      if (this.helper && this.helper.isOpen()) this.helper.close();
    }

    goTo(index) {
      if (!this.helper || !this.helper.isOpen()) return;
      if (index === this.currentIndex || !this.cards[index]) return;
      /* Offenes Picker-Sheet zurücksetzen, ohne den gleich ersetzten Trigger zu fokussieren und ohne Resume
         (beginStory startet die neue Card frisch). */
      const wasPopoverOpen = this.popoverOpen;
      this.closePopover({ focusTrigger: false, resume: false });
      /* War ein Sheet offen, war der Trap auf es begrenzt und closePopover({focusTrigger:false}) re-armt bewusst
         nicht. Produkt-Cards re-armen erst im async loadPanel — bis dahin zeigen die gecachten first/last auf die
         geschlossenen/disabled Radios. Sofort auf ein stabiles Ziel verankern; showCard/loadPanel re-armt final. */
      if (wasPopoverOpen) {
        this.rearmTrap(this._stableTrapTarget());
      }
      /* Auslaufende Progress-Schleife SYNCHRON stoppen — nicht auf das async 'pause' verlassen */
      this.stopProgress();
      /* Pending Submit + Picker-Resume sind card-scoped: beim Verlassen der Card verwerfen, sonst schließt ein
         spät auflösender cartUpdate der ALTEN Card die Modal / resumed eine stale Card. */
      this.pendingVariantId = null;
      this.pendingSubmitButton = null;
      this._resumeAfterPicker = false;
      this._pendingPickerFetch = null;
      this._panelFetchToken = null;
      this.showCard(index);
      this.beginStory();
      this.dispatch('ugc-story:cardchange');
    }

    next() {
      const last = this.cards.length - 1;
      if (this.currentIndex < last) {
        this.goTo(this.currentIndex + 1);
      } else if (this.endBehavior === 'loop') {
        /* goTo(0) wäre bei einer einzigen Card no-op (index === currentIndex) → replay */
        if (this.cards.length === 1) this.replayCurrent();
        else this.goTo(0);
      }
      /* stop: am Array-Ende no-op — letztes Segment bleibt voll, Video pausiert */
    }

    prev() {
      if (this.currentIndex > 0) {
        this.goTo(this.currentIndex - 1);
      } else if (this.endBehavior === 'loop') {
        if (this.cards.length === 1) this.replayCurrent();
        else this.goTo(this.cards.length - 1);
      }
    }

    showCard(index) {
      this.currentIndex = index;
      const card = this.cards[index];

      this.fillMeta(card);

      const hasProduct = Boolean(card.product && (card.product.url || card.product.handle));
      this.classList.toggle('ugc-story-modal--video-only', !hasProduct);
      this.classList.remove('ugc-story-modal--panel-failed');

      this.mountVideo(index);

      if (hasProduct) {
        /* Story-Switch: altes Panel SOFORT leeren, damit keine stale Controls (Farb-/Größen-Radios,
           Produktlink) während des neuen Section-Fetch interaktiv bleiben — lockPanel sperrt nur CTA/id-Input.
           Gilt NUR für den Story-Wechsel; der Variant-Refetch (Farbe/Größe) behält das alte Panel gesperrt
           sichtbar (kein Flackern). clearPanel entfernt evtl. fokussierte Controls → Trap auf eine stabile
           Schaltfläche neu verankern, wenn offen; loadPanel zeigt direkt den Spinner. */
        this.clearPanel();
        /* Trap-Grenzen nach dem Leeren neu rechnen (Panel-Controls sind weg), aber den Fokus NICHT hart
           auf Close ziehen: rearmTrap(null) behält gültigen Fokus (z.B. den Nav-Button beim Auto-Advance)
           und fällt nur auf den Content-Wrapper zurück, wenn der fokussierte Panel-Control entfernt wurde.
           loadPanel re-armt nach dem Inject erneut. */
        if (this.helper && this.helper.isOpen()) this.rearmTrap(null);
        this.loadPanel(card);
      } else {
        /* Laufenden Panel-Fetch abbrechen, bevor wir auf eine produktlose Story wechseln — sonst
           re-injiziert ein spät auflösender Fetch das alte Produktpanel in die --video-only-Modal
           (clearPanel leert nur den Slot). Wie handleClose; NICHT in clearPanel() (das ruft auch der
           Error-Pfad von loadPanel auf). */
        if (this.panelAbort) {
          this.panelAbort.abort();
          this.panelAbort = null;
        }
        this.clearPanel();
        /* Live-Switch auf eine produktlose Story: clearPanel entfernt die fokussierten Panel-Controls
           → Trap auf eine stabile Schaltfläche neu verankern (wie der Fehlerpfad von loadPanel).
           NUR wenn offen — rearmTrap fällt sonst auf focusEl.focus() zurück, und beim initialen openAt
           läuft showCard VOR helper.open, was den Fokus vorzeitig auf Close zöge. */
        if (this.helper && this.helper.isOpen()) {
          this.rearmTrap(this._stableTrapTarget());
        }
      }

      /* Nach dem Mount der aktiven Card das nächste Video vorpuffern (Background) */
      this.prebufferNext();
    }

    handleClose() {
      this.stopProgress();
      if (this.panelAbort) {
        this.panelAbort.abort();
        this.panelAbort = null;
      }
      this.releaseVideo();
      this.clearPanel();
      this.classList.remove(
        'ugc-story-modal--video-only',
        'ugc-story-modal--panel-failed',
        'ugc-story-modal--paused',
        'ugc-story-modal--playing'
      );
      this.currentIndex = -1;
      this.currentVideo = null;
      this.pendingVariantId = null;
      this.pendingSubmitButton = null;
      this.activePicker = null;
      this._resumeAfterPicker = false;
      this._pendingPickerFetch = null;
      this._panelFetchToken = null;
      this.dispatch('ugc-story:close');
    }

    dispatch(name, detail = {}) {
      this.dispatchEvent(
        new CustomEvent(name, { bubbles: true, detail: Object.assign({ index: this.currentIndex }, detail) })
      );
    }

    /* ── Video-Lifecycle (Andockpunkte für Preloading/Auto-Advance) ── */

    mountVideo(index) {
      if (!this.video) return;
      /* Switch-Guard: das wiederverwendete <video> feuert beim Quellentausch asynchron pause/ended/
         error für die ALTE Quelle. Operativ schützen die Element-Zustands-Checks in den Handlern
         (video.ended/paused/error werden von load() zurückgesetzt) + currentVideo/_forceTimer;
         _switching ist die synchrone Zusatzsicherung über den Tausch. */
      this._switching = true;
      this._forceTimer = false;
      this._autoplayFallback = false;
      this.video.pause();
      this.video.querySelectorAll('source').forEach((source) => source.remove());
      this.video.removeAttribute('poster');

      const card = this.cards[index];
      this.currentVideo = normalizeVideo(card);

      /* Ohne Video den Play-Button hart deaktivieren: getFocusableElements nutzt button:enabled
         und ignoriert CSS-Sichtbarkeit — ein nur per CSS verstecktes, enabled Control würde
         sonst als trapFocus-Grenze mitgezählt. Mit Video wieder aktivieren. */
      if (this.playButton) {
        this.playButton.disabled = !this.currentVideo;
        this.playButton.setAttribute('aria-hidden', String(!this.currentVideo));
      }

      /* Kein Video, aber Card-Image vorhanden → statisches Bild statt schwarzer Bühne;
         Play/Autoplay bleiben aus (playVideo/togglePlay sind ohne currentVideo No-ops). */
      const imageOnly = !this.currentVideo && card && typeof card.image === 'string' && card.image !== '';
      this.classList.toggle('ugc-story-modal--image-only', imageOnly);
      this.classList.toggle('ugc-story-modal--no-video', !this.currentVideo && !imageOnly);
      if (this.stageImage) {
        if (imageOnly) {
          this.stageImage.src = card.image;
          this.stageImage.hidden = false;
        } else {
          this.stageImage.hidden = true;
          this.stageImage.removeAttribute('src');
        }
      }

      if (!this.currentVideo) {
        this.video.preload = 'metadata';
        this.video.load();
        /* Mute-UI auch ohne Video synchronisieren (image-only/no-video, inkl. goTo/Pagination),
           sonst bleibt der Button vom vorigen Video-Card-Zustand stehen. */
        this.applyMuteState();
        this.syncPlayUI();
        this._switching = false;
        return;
      }

      if (this.currentVideo.poster) this.video.setAttribute('poster', this.currentVideo.poster);
      this.currentVideo.sources.forEach((entry) => {
        const source = document.createElement('source');
        source.src = entry.url;
        /* type nur setzen, wenn mime bekannt ist — sonst bestimmt der Browser den Typ */
        if (entry.mime) source.type = entry.mime;
        this.video.appendChild(source);
      });

      /* Auto-Advance statt Loop: einmal abspielen, das 'ended'-Event schaltet weiter
         (onVideoEnded → handleStoryEnd). loop explizit aus (defensiv gegen Dawn-Defaults). */
      this.video.loop = false;
      /* Aktives Video aktiv vorladen → nächstes liegt aus dem Prebuffer schon im Cache (kein Stocken) */
      this.video.preload = 'auto';
      this.video.load();
      this.applyMuteState();
      this.syncPlayUI();
      this._switching = false;
    }

    /* Nächstes Video im Hintergrund vorladen (preload="auto" am versteckten Prebuffer-Element),
       damit der Auto-Advance ohne sichtbares Buffering startet. Respektiert endBehavior: kein
       nächstes Video (stop am Ende, identische Single-Card im loop, image-only/no-video) →
       Prebuffer freigeben. */
    prebufferNext() {
      if (!this.prebufferVideo) return;
      const last = this.cards.length - 1;
      let nextIndex = this.currentIndex + 1;
      if (nextIndex > last) nextIndex = this.endBehavior === 'loop' ? 0 : -1;

      const nextVideo =
        nextIndex >= 0 && nextIndex !== this.currentIndex ? normalizeVideo(this.cards[nextIndex]) : null;
      if (!nextVideo) {
        this.releasePrebuffer();
        return;
      }

      /* Dieselbe Quelle schon gepuffert → kein redundantes load() */
      const firstUrl = (nextVideo.sources[0] && nextVideo.sources[0].url) || '';
      if (this.prebufferVideo.dataset.ugcPrebufferedUrl === firstUrl) return;

      this.releasePrebuffer();
      nextVideo.sources.forEach((entry) => {
        const source = document.createElement('source');
        source.src = entry.url;
        if (entry.mime) source.type = entry.mime;
        this.prebufferVideo.appendChild(source);
      });
      this.prebufferVideo.dataset.ugcPrebufferedUrl = firstUrl;
      this.prebufferVideo.load();
    }

    /* Prebuffer-Quelle + Puffer freigeben: load() ohne <source> bricht das Netzwerk ab und leert
       den Buffer (kein Leak beim langen Durchblättern). */
    releasePrebuffer() {
      if (!this.prebufferVideo) return;
      this.prebufferVideo.removeAttribute('src');
      this.prebufferVideo.querySelectorAll('source').forEach((source) => source.remove());
      delete this.prebufferVideo.dataset.ugcPrebufferedUrl;
      this.prebufferVideo.load();
    }

    playVideo() {
      if (!this.video || !this.helper || !this.helper.isOpen()) return;
      if (!this.currentVideo) return;
      /* Play-Request-Token: eine spät auflösende Ablehnung einer FRÜHEREN Card darf den Fallback nicht
         auf der aktuellen Card scharf schalten (currentVideo ist pro mountVideo ein neues Objekt). */
      const reqVideo = this.currentVideo;
      const reqIndex = this.currentIndex;
      const playPromise = this.video.play();
      if (playPromise && typeof playPromise.then === 'function') {
        playPromise
          .then(() => {
            /* Robuster Start der Video-Progression: nicht allein aufs 'play'-Event verlassen (kann je nach
               Browser/Timing ausbleiben). Nur wenn dieselbe Story noch offen ist, das Video wirklich läuft,
               kein Medienfehler vorliegt und der Timer-Modus den Fortschritt NICHT besitzt (_autoplayFallback
               wird auf einem echten Play zuvor von onVideoPlay verworfen). startProgress ist idempotent
               (stopProgress vorab), doppelter Start mit dem 'play'-Pfad ist harmlos. */
            if (
              this.helper &&
              this.helper.isOpen() &&
              this.currentVideo === reqVideo &&
              this.currentIndex === reqIndex &&
              this.video &&
              !this.video.paused &&
              !this.video.error &&
              !this._timerMode()
            ) {
              this.startProgress();
            }
          })
          .catch((error) => {
            this.syncPlayUI();
            /* Nur eine echte Autoplay-Policy-Ablehnung (NotAllowedError) bekommt den Timer-Fallback, damit die
               Story trotzdem weiterläuft (AC). AbortError/„interrupted" = absichtliche Unterbrechung durch
               pause()/load() (z.B. Card-Switch) → KEIN Fallback. Zusätzlich nur, wenn dieselbe Story offen ist,
               das Video pausiert und kein Medienfehler vorliegt (sonst übernimmt onVideoError via _forceTimer).
               Cancelbar via onVideoPlay. */
            if (
              error &&
              error.name === 'NotAllowedError' &&
              this.helper &&
              this.helper.isOpen() &&
              this.currentVideo === reqVideo &&
              this.currentIndex === reqIndex &&
              this.video &&
              this.video.paused &&
              !this.video.error
            ) {
              this._autoplayFallback = true;
              this.startProgress();
            }
          });
      }
    }

    pauseVideo() {
      if (this.video) this.video.pause();
    }

    resetVideo() {
      if (this.video && this.video.readyState > 0) this.video.currentTime = 0;
    }

    releaseVideo() {
      /* Beim Schließen: Video stoppen (kein weiterlaufender Ton) und Quelle/Puffer freigeben —
         <source> + src entfernen, load() bricht Netzwerk ab und leert den Decode-Buffer (kein Leak). */
      this.pauseVideo();
      this.resetVideo();
      if (this.video) {
        this.video.querySelectorAll('source').forEach((source) => source.remove());
        this.video.removeAttribute('src');
        this.video.removeAttribute('poster');
        this.video.preload = 'metadata';
        this.video.load();
      }
      this.releasePrebuffer();
      /* Image-only-Fallback zentral aufräumen (greift bei close UND disconnectedCallback). */
      if (this.stageImage) {
        this.stageImage.hidden = true;
        this.stageImage.removeAttribute('src');
      }
      this.classList.remove('ugc-story-modal--image-only', 'ugc-story-modal--no-video');
    }

    togglePlay() {
      if (!this.video || !this.currentVideo) return;
      /* Manuelles Play/Pause ist ein expliziter Nutzer-Override: ausstehendes Picker-Resume UND den laufenden
         Picker-Fetch-Token verwerfen, damit ein späteres Settlement die manuelle Entscheidung NICHT überstimmt. */
      this._resumeAfterPicker = false;
      this._pendingPickerFetch = null;
      if (this.video.paused) {
        this.playVideo();
      } else {
        this.pauseVideo();
      }
    }

    syncPlayUI() {
      if (!this.playButton || !this.video) return;
      const paused = this.video.paused;
      /* State-Klassen für die Overlay-Gradient-Umschaltung (CSS-only, kein Verhalten):
         --paused treibt den Top+Bottom-Gradient; --playing ist nur der Default-Marker (kein CSS-Konsument). */
      this.classList.toggle('ugc-story-modal--paused', paused);
      this.classList.toggle('ugc-story-modal--playing', !paused);
      const icon = this.playButton.querySelector('.ugc-story-modal__play-icon');
      if (icon) icon.hidden = !paused;
      const label = paused ? this.playButton.dataset.labelPlay : this.playButton.dataset.labelPause;
      if (label) this.playButton.setAttribute('aria-label', label);
    }

    toggleMute() {
      this.muted = !this.muted;
      this.applyMuteState();
    }

    applyMuteState() {
      if (this.video) {
        /* Property UND Attribut — iOS bewertet das Attribut für die Autoplay-Policy */
        this.video.muted = this.muted;
        this.video.toggleAttribute('muted', this.muted);
      }
      if (!this.muteButton) return;
      /* Ohne Videoquelle ist Stummschalten bedeutungslos → Button wie den Play-Button hart
         deaktivieren (disabled + aria-hidden), damit kein verwaister, fokussierbarer Toggle
         über fehlendem Audio bleibt; mit Video wieder aktivieren. */
      const hasVideo = !!this.currentVideo;
      this.muteButton.disabled = !hasVideo;
      this.muteButton.setAttribute('aria-hidden', String(!hasVideo));
      /* Toggle-Button: aria-pressed trägt den Zustand; Name (aria-label/title) bleibt stabil "Mute" im Markup */
      this.muteButton.setAttribute('aria-pressed', String(this.muted));
      const iconOff = this.muteButton.querySelector('.ugc-story-modal__mute-icon--off');
      const iconOn = this.muteButton.querySelector('.ugc-story-modal__mute-icon--on');
      if (iconOff) iconOff.hidden = !this.muted;
      if (iconOn) iconOn.hidden = this.muted;
    }

    /* ── Story-Pagination / Auto-Advance ───────────────────────────── */

    /* Segmente einmalig bauen (= Anzahl Cards). Einzelne Story OHNE loop: Nav-Buttons disabled
       (raus aus dem Fokus-Trap) + via CSS versteckt — nichts zu navigieren. Mit loop bleiben sie
       bedienbar (replayCurrent), passend zu Tap-Zonen/Pfeiltasten. Einmalige Entscheidung
       (endBehavior ist pro Session fix) → kein per-Card-Toggle, kein Trap-Churn. */
    buildPagination() {
      if (this._paginationBuilt || !this.pagination) return;
      const count = this.cards.length;
      if (count < 2 && this.endBehavior !== 'loop') {
        if (this.prevButton) this.prevButton.disabled = true;
        if (this.nextButton) this.nextButton.disabled = true;
      }
      const fragment = document.createDocumentFragment();
      for (let i = 0; i < count; i++) {
        const segment = document.createElement('div');
        segment.className = 'ugc-story-modal__pagination-segment';
        const fill = document.createElement('div');
        fill.className = 'ugc-story-modal__pagination-fill';
        segment.appendChild(fill);
        fragment.appendChild(segment);
      }
      this.pagination.appendChild(fragment);
      this._paginationBuilt = true;
    }

    /* Segmente vor dem aktiven voll, danach leer; aktives = ratio (reduced motion: voll).
       Liest currentIndex als Source of Truth — ein Panel-Re-Fetch (Größe/Farbe) ändert
       die Pagination NICHT, nur ein echter Card-Switch (goTo/beginStory) ruft das hier. */
    paintPagination(activeIndex, ratio) {
      if (!this.pagination) return;
      const reduced = prefersReducedMotion();
      const segments = this.pagination.children;
      this._activeFill = null;
      for (let i = 0; i < segments.length; i++) {
        const segment = segments[i];
        const fill = segment.querySelector('.ugc-story-modal__pagination-fill');
        const isActive = i === activeIndex;
        segment.classList.toggle('is-active', isActive);
        if (!fill) continue;
        if (i < activeIndex) {
          fill.style.setProperty('--fill', '1');
        } else if (i > activeIndex) {
          fill.style.setProperty('--fill', '0');
        } else {
          fill.style.setProperty('--fill', reduced ? '1' : String(ratio));
          this._activeFill = fill;
        }
      }
    }

    setActiveFill(ratio) {
      if (this._activeFill) this._activeFill.style.setProperty('--fill', String(ratio));
    }

    /* a11y: „Story n von N" in die Live-Region (die Pagination selbst ist aria-hidden) */
    updateProgressStatus() {
      if (!this.progressStatus) return;
      const template = this.pagination && this.pagination.dataset.progressLabel;
      if (!template) return;
      this.progressStatus.textContent = template
        .replace('[index]', String(this.currentIndex + 1))
        .replace('[total]', String(this.cards.length));
    }

    /* a11y: Server-Preis nach einem Nutzer-Variantenwechsel in die stabile Preis-Live-Region schreiben.
       Der Panel-Preis (role=status) wird bereits befüllt injiziert und kündigt daher nicht an; diese Region
       existiert stabil außerhalb des ersetzten Panel-Targets und kündigt die Änderung an. Wird NUR aus dem
       Radio-Change-Pfad (announcePrice) gerufen, nie beim Story-Switch/Initial-Load. Whitespace normalisiert;
       die sichtbare Preisanzeige bleibt serverseitig (kein JS-Patch des Preises). */
    announcePrice() {
      if (!this.priceStatus || !this.panelTarget) return;
      const priceEl = this.panelTarget.querySelector('.ugc-story-panel__price');
      const text = priceEl ? priceEl.textContent.replace(/\s+/g, ' ').trim() : '';
      if (!text) return;
      this.priceStatus.textContent = text;
    }

    /* Nach jedem Card-Switch (und beim Öffnen): Pagination/Status setzen, Wiedergabe starten.
       Wird NUR aus openAt/goTo/replayCurrent gerufen, nie aus loadPanel (Variant-Re-Fetch). */
    beginStory() {
      this.stopProgress();
      this.paintPagination(this.currentIndex, 0);
      this.updateProgressStatus();
      this.updateNavState();
      if (this.currentVideo && !this._forceTimer) {
        /* Reduced Motion: kein Autoplay (= Bewegung); nach manuellem Play schaltet 'ended' weiter */
        if (prefersReducedMotion()) {
          this.syncPlayUI();
        } else {
          this.playVideo();
        }
      } else {
        /* Kein abspielbares Video (image-only, oder Replay einer kaputten Quelle mit _forceTimer):
           timergesteuerte Füllung + Auto-Advance. Bei reduced motion KEIN Auto-Timer — nur manuelle
           Navigation (ein manuell gestartetes Video läuft weiter über 'ended'). */
        if (!prefersReducedMotion()) this.startProgress();
      }
    }

    /* rAF-Fortschritt. Video-Modus liest currentTime/duration (selbstkorrigierend nach Tab-Throttle);
       Timer-Modus (image-only / Video-Fehler) zählt geclampte Frame-Deltas. Idempotent. */
    startProgress() {
      if (!this.helper || !this.helper.isOpen()) return;
      /* stopProgress ist der EINZIGE Reset-Punkt (nullt elapsed/lastTs) und läuft vor jedem
         reduced-motion-Early-Return. */
      this.stopProgress();
      /* Echtes Video bei reduced motion: keine flüssige Füllung; 'ended' übernimmt das Weiterschalten.
         Timer-Modus läuft auch bei reduced motion — aber unter reduced motion erreicht nur der Fehler-/
         Autoplay-Block-Pfad startProgress (_forceTimer via onVideoError, _autoplayFallback via playVideo-catch);
         image-only wird in beginStory gegated und advanced dann NICHT automatisch. */
      if (prefersReducedMotion() && !this._timerMode()) return;
      this._progressRaf = requestAnimationFrame(this.tick);
    }

    tick(timestamp) {
      const reduced = prefersReducedMotion();
      const timerMode = this._timerMode();
      if (timerMode) {
        if (this._progressLastTs) {
          /* Delta clampen, damit ein Hintergrund-Tab-Sprung den Timer nicht durchschießt */
          this._progressElapsed += Math.min(timestamp - this._progressLastTs, 100);
        }
        this._progressLastTs = timestamp;
        const ratio = Math.min(this._progressElapsed / STORY_IMAGE_DURATION, 1);
        if (!reduced) this.setActiveFill(ratio);
        if (ratio >= 1) {
          this.stopProgress();
          this.handleStoryEnd();
          return;
        }
      } else {
        const duration = this.video.duration;
        /* duration ist bei preload="none" bis loadedmetadata NaN → diesen Frame nicht malen */
        if (isFinite(duration) && duration > 0 && !reduced) {
          this.setActiveFill(Math.min(this.video.currentTime / duration, 1));
        }
        /* Weiterschalten kommt aus dem 'ended'-Event, nicht hier */
      }
      this._progressRaf = requestAnimationFrame(this.tick);
    }

    stopProgress() {
      if (this._progressRaf) {
        cancelAnimationFrame(this._progressRaf);
        this._progressRaf = null;
      }
      this._progressElapsed = 0;
      this._progressLastTs = 0;
    }

    /* Card-Ende → next() (Boundary/Loop inkl. N=1 ist dort gekapselt). Auto-Advance darf eine aktive
       Produkt-Interaktion NICHT abbrechen: bei offenem Größen-Popover oder laufendem Add-to-Cart bleibt
       die Story stehen (Segment voll, Fortschritt ist bereits gestoppt). Manuelle Navigation (next/prev)
       umgeht das hier und funktioniert weiter. Sichere Default-Policy. */
    handleStoryEnd() {
      if (this._isInteracting()) return;
      this.next();
    }

    /* Auto-Advance pausieren NUR bei echter Interaktion: offener Größen-Popover oder ein noch laufender
       Add-to-Cart. KEIN panelSlot-Fokus-Check — geparkter Fokus (z.B. die gewählte Pille nach einem
       Variant-Wechsel) darf die Story nicht dauerhaft anhalten. Echtes Pause/Resume bei focus/blur ggf. später. */
    _isInteracting() {
      return this.popoverOpen || this._isSubmitPending();
    }

    /* „Submit in flight": pendingVariantId gesetzt UND die gemerkte CTA hängt noch im DOM und ist im
       .loading-Zustand. product-form.js entfernt .loading auf ALLEN Settle-Pfaden (finally — inkl.
       Netzwerk-Fehler, der weder cartError noch cartUpdate publiziert). Sonst (settled, oder Panel neu
       gerendert → CTA disconnected) freigeben: verhindert das unbegrenzte Hängen des Auto-Advance. */
    _isSubmitPending() {
      if (!this.pendingVariantId) return false;
      const cta = this.pendingSubmitButton;
      if (cta && cta.isConnected && cta.classList.contains('loading')) return true;
      this.pendingVariantId = null;
      this.pendingSubmitButton = null;
      return false;
    }

    /* Einzige Autorität, ob der Fortschritt timergesteuert läuft (statt über die Videozeit). */
    _timerMode() {
      return !this.currentVideo || this._forceTimer || this._autoplayFallback;
    }

    /* Tap-Zonen pro Card/Index synchronisieren: die nicht verfügbare Seite bekommt pointer-events:none
       (CSS), damit ein toter Tap das Play/Pause-Overlay darunter erreicht. Bei N=1+stop sind beide
       Seiten unavailable → Taps gehen ans Play/Pause. */
    updateNavState() {
      const last = this.cards.length - 1;
      const loop = this.endBehavior === 'loop';
      const prevAvail = this.currentIndex > 0 || loop;
      const nextAvail = this.currentIndex < last || loop;
      if (this.tapPrev) this.tapPrev.classList.toggle('ugc-story-modal__tapzone--unavailable', !prevAvail);
      if (this.tapNext) this.tapNext.classList.toggle('ugc-story-modal__tapzone--unavailable', !nextAvail);
      /* Echte Nav-Buttons (Desktop/Tastatur/AT) dieselbe Verfügbarkeit geben: aria-disabled statt disabled
         → bleibt fokussierbar, kein Trap-Churn (Click-Handler ruft dann kein prev/next). N=1+stop bleibt
         zusätzlich via buildPagination hart disabled + versteckt. */
      if (this.prevButton) this.prevButton.setAttribute('aria-disabled', String(!prevAvail));
      if (this.nextButton) this.nextButton.setAttribute('aria-disabled', String(!nextAvail));
    }

    /* Einzige Story + loop: gleiche Card neu starten (goTo(0) wäre no-op via index===currentIndex).
       Nur den (erholbaren) Autoplay-Fallback zurücksetzen — ein echter Medienfehler (_forceTimer)
       bleibt bestehen, damit die kaputte Quelle weiter über den Timer loopt. */
    replayCurrent() {
      this.stopProgress();
      this._autoplayFallback = false;
      /* Läuft das Video noch (N=1 + loop), würde playVideo()→video.play() KEIN neues 'play'-Event feuern
         (kein paused→playing-Übergang) → onVideoPlay startet die rAF nicht neu, der Progress bliebe hängen.
         Vor dem Neustart pausieren, damit beginStory()→playVideo() den normalen play-Pfad auslöst.
         _forceTimer bleibt unangetastet (kaputte Quelle loopt weiter über den Timer). */
      this.pauseVideo();
      this.resetVideo();
      this.beginStory();
    }

    /* Wiederverwendetes <video>: stale pause/ended/error der ALTEN Quelle dürfen die Progress-Engine
       der neuen Card nicht stoppen oder doppelt weiterschalten. Operative Guards: Element-Zustand
       (video.ended/paused/error — von load() zurückgesetzt) + currentVideo/_forceTimer (der
       Timer-Modus gehört dem Timer-Pfad) + helper.isOpen; _switching ist die synchrone Zusatzsicherung. */
    onVideoPlay() {
      this.syncPlayUI();
      if (this._switching) return;
      /* Echtes Abspielen erkannt → den erholbaren Autoplay-Fallback verwerfen und auf normale
         Video-Progression zurück (nur bei abspielbarem Video: kein Medienfehler, kein _forceTimer). */
      if (this._autoplayFallback && this.video && !this.video.error && !this._forceTimer) {
        this._autoplayFallback = false;
      }
      if (this.currentVideo && !this._timerMode()) this.startProgress();
    }

    onVideoPause() {
      this.syncPlayUI();
      /* Im Timer-Modus (image-only / _forceTimer / _autoplayFallback) gehört der Fortschritt dem Timer —
         ein pause-Event des pausierten/blockierten/kaputten Videos darf ihn NICHT stoppen. */
      if (!this._switching && !this._timerMode() && this.video.paused) this.stopProgress();
    }

    onVideoEnded() {
      if (this._switching || !this.video || !this.video.ended || !this.helper || !this.helper.isOpen()) return;
      this.stopProgress();
      this.setActiveFill(1);
      /* Korrekten Play/Replay-Zustand zeigen — wichtig im stop-Endzustand (kein Advance) */
      this.syncPlayUI();
      this.handleStoryEnd();
    }

    onVideoError() {
      if (this._switching || !this.currentVideo || !this.video || !this.video.error || !this.helper || !this.helper.isOpen())
        return;
      /* Echte Quelle der aktuellen Story kaputt → Timer-Fallback, damit die Story nicht hängenbleibt;
         image-only startet seinen Timer dagegen aus beginStory(), nicht aus Error-Events. */
      this._forceTimer = true;
      /* Reduced Motion: kaputte Quelle NICHT automatisch weiterschalten (konsistent mit dem image-only-
         Pfad, der in beginStory gegated ist) — nur manuelle Navigation; Play/Replay-UI synchronisieren.
         _forceTimer bleibt gesetzt: re-nav/replay geht über den else-Zweig in beginStory (kein playVideo
         auf der kaputten Quelle), der unter reduced motion ebenfalls keinen Timer startet. */
      if (prefersReducedMotion()) {
        this.syncPlayUI();
        return;
      }
      this.startProgress();
    }

    /* ── Meta-Overlay (UGC-Inhalte, XSS-sicher via textContent) ────── */

    fillMeta(card) {
      const metaEl = this.querySelector('.ugc-story-modal__meta');
      if (metaEl) {
        const hasMeta = Boolean(card.social_name || card.verified === true || card.subtitle || card.views_label);
        metaEl.hidden = !hasMeta;
      }

      const socialEl = this.querySelector('[data-ugc-social]');
      if (socialEl) socialEl.textContent = card.social_name || '';

      const verifiedEl = this.querySelector('[data-ugc-verified]');
      if (verifiedEl) verifiedEl.hidden = card.verified !== true;

      const subtitleEl = this.querySelector('[data-ugc-subtitle]');
      if (subtitleEl) {
        subtitleEl.textContent = card.subtitle || '';
        subtitleEl.hidden = !card.subtitle;
      }

      const viewsEl = this.querySelector('[data-ugc-views]');
      if (viewsEl) {
        viewsEl.textContent = card.views_label || '';
        viewsEl.hidden = !card.views_label;
      }
    }

    /* ── Produktpanel: lazy + variant-aware + server-rendered ──────── */

    async loadPanel(card, options = {}) {
      const product = card.product;
      if (!product || !this.panelTarget) return;

      const variantId = options.variantId || product.variant_id;
      const focusVariantId = options.focusVariantId || null;

      if (this.panelAbort) this.panelAbort.abort();
      this.panelAbort = new AbortController();
      const signal = this.panelAbort.signal;

      /* Altes Panel sperren, bevor der Re-Fetch läuft — kein Submit mit stalem Variant-Id */
      this.lockPanel();
      this.setPanelLoading(true);

      /* Stale-Panel-Interlock vor dem ersten await setzen: blockiert eine zweite Picker-Auswahl, solange
         dieser Fetch läuft (siehe change-Handler-Guard). finally löscht ihn token-match → übersteht togglePlay. */
      const fetchToken = {};
      this._panelFetchToken = fetchToken;

      try {
        let url;
        if (product.url) {
          url = new URL(product.url, window.location.origin);
        } else {
          /* Handle-Fallback locale-aware über Shopify.routes.root (Muster: size-chart-modal.js) */
          const root = (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
          url = new URL(root + 'products/' + product.handle, window.location.origin);
        }
        if (variantId) url.searchParams.set('variant', variantId);
        url.searchParams.set('section_id', 'ugc-story-panel');

        const response = await fetch(url.pathname + url.search, { signal });
        if (!response.ok) throw new Error('HTTP ' + response.status);
        const html = await response.text();
        if (signal.aborted) return;

        if (typeof HTMLUpdateUtility !== 'undefined' && HTMLUpdateUtility.setInnerHTML) {
          HTMLUpdateUtility.setInnerHTML(this.panelTarget, html);
        } else {
          this.panelTarget.innerHTML = html;
        }

        this.fillStars(card);

        /* Datora Discount-Preise/Badge auf dem frisch gerenderten Panel aktualisieren. ASYNC: die Funktion
           awaitet co2 (getDiscountCode/itemDiscountFull) und schreibt ERST DANACH den rabattierten Preis
           (assets/datora.js). Promise festhalten, um die a11y-Ansage daran zu hängen. */
        const discountUpdate =
          typeof updateProductGridDiscounts === 'function' ? updateProductGridDiscounts(this.panelTarget) : null;

        /* a11y: nur bei explizitem Nutzer-Variantenwechsel ansagen — und erst NACHDEM das (asynchrone)
           Discount-Update gesettled ist, sonst träfe die Ansage den Server-Basispreis statt des rabattierten.
           An das Promise hängen statt zu awaiten, damit Panel-UI/Fokus/Trap/Spinner NICHT blockieren (nur die
           Hintergrund-Ansage wartet). Erfolg ODER Fehler → ansagen: bei co2-Fehler bleibt der sichtbare Server-
           Preis, der dann korrekt angesagt wird. Ein überholter (abgebrochener) Fetch sagt nicht an. Story-
           Switch/Initial-Load (kein announcePrice) schweigt. */
        if (options.announcePrice) {
          const announceFinalPrice = () => {
            if (!signal.aborted) this.announcePrice();
          };
          Promise.resolve(discountUpdate).then(announceFinalPrice, announceFinalPrice);
        }

        /* Frisches Panel kommt kollabiert an: erst Trigger-/Radio-Sync (mobile), DANN Trap neu verankern —
           Reihenfolge tragend, sonst landen versteckte enabled-Radios in den first/last-Grenzen. Fokusziel:
           mobile → der Trigger des gewechselten Pickers (Radios sind kollabiert disabled), Desktop → die
           gewählte Option (Pill/Swatch). */
        this.activePicker = null;
        this.syncTriggers();
        if (this.isMobileViewport()) this.syncPopoverRadios(true);

        let focusEl = null;
        if (focusVariantId) {
          if (this.isMobileViewport()) {
            const els = options.focusPicker ? this._pickerEls(options.focusPicker) : null;
            focusEl = (els && els.trigger) || this.panelTarget.querySelector('[data-ugc-trigger]');
          } else {
            /* Auf den gewechselten Picker scopen: gewählte Farbe UND Größe können dieselbe aufgelöste
               variant-id tragen — der globale Lookup würde bei einem Größenwechsel sonst den Farb-Swatch
               fokussieren. Erst im Picker suchen, dann global als Fallback. */
            const scopedFocus = options.focusPicker
              ? this.panelTarget.querySelector(
                  '[data-ugc-picker="' + options.focusPicker + '"] input[data-variant-id="' + focusVariantId + '"]'
                )
              : null;
            focusEl = scopedFocus || this.panelTarget.querySelector('input[data-variant-id="' + focusVariantId + '"]');
          }
          if (!focusEl) focusEl = this.panelTarget.querySelector('[data-ugc-cta]');
        }
        this.rearmTrap(focusEl);
        /* Gesettled (nicht abgebrochen): signalisiert dem Variant-Wechsel-Aufrufer, dass jetzt — nach dem
           Inject — eine vor dem Sheet pausierte Story fortgesetzt werden darf. Ein abgebrochener Fetch
           (von einem neueren Wechsel ersetzt) erreicht dieses return nicht und setzt daher nicht fort. */
        return true;
      } catch (error) {
        if (error.name === 'AbortError') return;
        console.error('UGC story modal: Panel-Fetch fehlgeschlagen', error);
        /* Video ist der Hauptinhalt — Modal offen lassen, Panel ausblenden. activePicker zuerst nullen:
           clearPanel entfernt die Picker-DOM, popoverOpen darf danach nicht true bleiben (Reopen→Fehler). */
        this.activePicker = null;
        this.clearPanel();
        this.classList.add('ugc-story-modal--panel-failed');
        /* clearPanel entfernt die fokussierten Panel-Controls → Trap auf eine stabile
           verbleibende Modal-Schaltfläche neu verankern (Close), sonst zeigen die
           gecachten first/last-Grenzen ins Leere und Tab entkommt dem Dialog. */
        this.rearmTrap(this._stableTrapTarget());
        /* Fetch-Fehler ist ein (nicht abgebrochenes) Settle: das Story-Medium läuft unabhängig vom Panel,
           also darf eine vor dem Sheet pausierte Story wieder fortgesetzt werden. */
        return true;
      } finally {
        if (!signal.aborted) this.setPanelLoading(false);
        /* Interlock nur lösen, wenn KEIN neuerer Fetch ihn übernommen hat (token-match) — sonst gibt ein
           abgebrochener älterer Fetch die Sperre frei, während der neuere noch läuft. */
        if (this._panelFetchToken === fetchToken) this._panelFetchToken = null;
      }
    }

    /* ── Mobile Größen-Popover ─────────────────────────────────────── */

    isMobileViewport() {
      return this.mobileMql.matches;
    }

    /* Alle Picker (Farbe/Größe) im aktuellen Panel. */
    _pickers() {
      return this.panelTarget ? Array.from(this.panelTarget.querySelectorAll('[data-ugc-picker]')) : [];
    }

    /* Einen Picker zu {picker, trigger, popover} auflösen (null, wenn nicht vorhanden). */
    _pickerEls(key) {
      const picker = key && this.panelTarget && this.panelTarget.querySelector('[data-ugc-picker="' + key + '"]');
      if (!picker) return null;
      return {
        picker: picker,
        trigger: picker.querySelector('[data-ugc-trigger]'),
        popover: picker.querySelector('[data-ugc-popover]'),
      };
    }

    /* Stabiles Trap-Ziel, wenn fokussierte Panel-Controls verschwinden: Close, Next, Content, Modal. */
    _stableTrapTarget() {
      return (
        this.querySelector('[data-modal-close]') ||
        this.nextButton ||
        this.querySelector('[data-ugc-modal-content]') ||
        this
      );
    }

    /* Kollabierte Radios EINES Sheets aktiv aus den Fokussierbaren nehmen: Dawn getFocusableElements()
       matcht input:enabled und ignoriert CSS-Hiding der Vorfahren. Server-disabled Radios (blank variant)
       tragen NIE den Marker und werden hier nie re-enabled. */
    _syncOnePopoverRadios(popover, collapsed) {
      if (!popover) return;
      popover.querySelectorAll('input[type="radio"]').forEach((input) => {
        if (collapsed) {
          if (!input.disabled) {
            input.disabled = true;
            input.setAttribute('data-ugc-temp-disabled', '');
          }
        } else if (input.hasAttribute('data-ugc-temp-disabled')) {
          input.disabled = false;
          input.removeAttribute('data-ugc-temp-disabled');
        }
      });
    }

    /* Alle Sheets kollabieren/auffalten (Aufrufer mit collapsed=true laufen ohne offenes Sheet). */
    syncPopoverRadios(collapsed) {
      this._pickers().forEach((picker) => {
        this._syncOnePopoverRadios(picker.querySelector('[data-ugc-popover]'), collapsed);
      });
    }

    /* Desktop versteckt die Trigger per CSS (display:none); getFocusableElements matcht aber button:enabled
       und ignoriert CSS-Hiding → ein enabled Trigger zählte sonst als Trap-Grenze. Mobile: Trigger sind die
       sichtbaren Schaltflächen und bleiben bedienbar. Gilt für alle Picker. */
    syncTriggers() {
      const mobile = this.isMobileViewport();
      this._pickers().forEach((picker) => {
        const trigger = picker.querySelector('[data-ugc-trigger]');
        if (trigger) trigger.disabled = !mobile;
      });
    }

    /* Öffnet das Sheet eines Pickers (Farbe/Größe). Es ist immer nur EINES offen — ein anderes offenes Sheet
       wird zuvor geschlossen (ohne Resume, da gleich neu pausiert wird). Öffnet das Sheet eine laufende Story,
       wird sie pausiert und beim Schließen wieder fortgesetzt. */
    openPopover(key) {
      /* Während eines laufenden Variant-Re-Fetch kein Sheet öffnen (zweite Auswahl wird ohnehin ignoriert). */
      if (this._panelFetchToken) return;
      const els = this._pickerEls(key);
      if (!els || !els.trigger || !els.popover) return;
      if (this.activePicker === key) return;

      /* Zuerst das NEUE Sheet prüfen (ohne das alte anzutasten): ohne wählbare Option KEIN leerer Trap —
         dann gar nicht öffnen und ein evtl. offenes anderes Sheet offen lassen. */
      this._syncOnePopoverRadios(els.popover, false);
      const focusOption =
        els.popover.querySelector('input[type="radio"]:checked:not(:disabled)') ||
        els.popover.querySelector('input[type="radio"]:not(:disabled)');
      if (!focusOption) {
        this._syncOnePopoverRadios(els.popover, true);
        return;
      }

      /* Resume-Intent ERHALTEN, wenn schon ein Sheet offen ist (Sheet-Wechsel) ODER ein verzögertes Resume
         noch aussteht (Reopen während eines laufenden Variant-Re-Fetch — die Story ist dann pausiert, also
         wäre _isAdvancing false und der erfasste Intent ginge verloren). Nur beim echten Erstöffnen frisch erfassen. */
      const wasAdvancing =
        this.activePicker !== null || this._resumeAfterPicker ? this._resumeAfterPicker : this._isAdvancing();
      if (this.activePicker) this.closePopover({ resume: false, focusTrigger: false });

      this.activePicker = key;
      els.popover.classList.add('is-open');
      els.trigger.setAttribute('aria-expanded', 'true');
      /* Echte Pause statt nur Auto-Advance-Suppression: Video/Timer einfrieren, solange das Sheet offen ist. */
      this.pauseStory();
      this._resumeAfterPicker = wasAdvancing;
      /* Trap auf das Sheet begrenzen — CTA/Produktlink dahinter sind für Tab nicht erreichbar. */
      this.rearmTrap(focusOption, els.popover);
    }

    /* Schließt das aktuell offene Sheet. resume=true (Default) setzt eine zuvor pausierte Story fort;
       Variant-Wechsel und der Geschwister-Auto-Close übergeben resume=false. */
    closePopover(options = {}) {
      const focusTrigger = options.focusTrigger !== false;
      const resume = options.resume !== false;
      const key = this.activePicker;
      if (!key) return;
      const els = this._pickerEls(key);
      this.activePicker = null;
      if (els && els.popover) {
        els.popover.classList.remove('is-open');
        this._syncOnePopoverRadios(els.popover, true);
      }
      if (els && els.trigger) els.trigger.setAttribute('aria-expanded', 'false');
      if (resume) this._maybeResumeAfterPicker();
      if (focusTrigger && els && els.trigger) this.rearmTrap(els.trigger);
    }

    /* ── Story-Pause/Resume (nur durch offene Picker-Sheets) ───────── */

    /* Läuft die Story gerade voran? Video: spielt tatsächlich (auch ein manuell gestartetes Video unter
       reduced motion zählt — reduced motion verhindert Autoplay, nicht das Fortsetzen). Timer: rAF aktiv
       (die Image-Füllung ist unter reduced motion gegated → null → false). Vor dem Pausieren erfasst,
       damit ein Sheet eine wirklich laufende Story fortsetzt, eine pausierte aber nicht. */
    _isAdvancing() {
      if (this._timerMode()) return this._progressRaf != null;
      return !!(this.video && !this.video.paused && !this.video.ended);
    }

    pauseStory() {
      if (this._timerMode()) this.pauseProgress();
      else this.pauseVideo();
    }

    resumeStory() {
      if (this._timerMode()) this.resumeProgress();
      else this.playVideo();
    }

    /* Timer-Modus einfrieren OHNE _progressElapsed zu nullen (stopProgress würde resetten) — nur
       _progressLastTs zurücksetzen, damit der erste Frame nach Resume keinen alten Delta addiert. */
    pauseProgress() {
      if (this._progressRaf) {
        cancelAnimationFrame(this._progressRaf);
        this._progressRaf = null;
      }
      this._progressLastTs = 0;
    }

    /* Timer-rAF fortsetzen OHNE stopProgress (der EINZIGE Arm, der den elapsed-Reset überspringt);
       behält _progressElapsed, mit derselben Reduced-Motion-Gate wie startProgress. */
    resumeProgress() {
      if (!this.helper || !this.helper.isOpen()) return;
      if (this._progressRaf) return;
      if (prefersReducedMotion() && !this._timerMode()) return;
      this._progressLastTs = 0;
      this._progressRaf = requestAnimationFrame(this.tick);
    }

    /* Setzt eine durch ein Sheet pausierte Story fort — nur, wenn beim Öffnen aktiv (kein Auto-Play einer
       manuell pausierten Story) und die Modal noch offen ist. Idempotent (Flag zuerst löschen). Einzige
       Resume-Entscheidungsstelle (closePopover/onMqlChange/Fetch-Settlement rufen hier durch): läuft noch ein
       Picker-getriebener Re-Fetch (_pendingPickerFetch gesetzt), besitzt DESSEN Settlement das Resume → hier
       armiert bleiben (Flag NICHT löschen); das Settlement nullt den Token vor dem Aufruf. */
    _maybeResumeAfterPicker() {
      if (!this._resumeAfterPicker) return;
      if (this._pendingPickerFetch) return;
      this._resumeAfterPicker = false;
      if (!this.helper || !this.helper.isOpen()) return;
      this.resumeStory();
    }

    /* trapFocus cached first/last — nach jeder DOM-/Fokussierbarkeits-Änderung neu
       verankern; Fallback-Fokus, wenn trapFocus (global.js) nicht verfügbar ist.
       container: optionaler Trap-Container (default = Modal-Content; offener Popover
       übergibt sich selbst, damit der Trap nicht hinter das Sheet greift). */
    rearmTrap(focusEl, container) {
      const trapContainer = container || this.querySelector('[data-ugc-modal-content]') || this;
      if (typeof trapFocus === 'function' && this.helper && this.helper.isOpen()) {
        const fallback = trapContainer.contains(document.activeElement) ? document.activeElement : trapContainer;
        trapFocus(trapContainer, focusEl || fallback);
      } else if (focusEl) {
        focusEl.focus();
      }
    }

    /* Sperrt das aktuelle Panel vor einem Variant-Re-Fetch: alte Form darf nicht mehr
       submitten (Enter/implizite Submission) während die neue Server-Antwort unterwegs ist. */
    lockPanel() {
      const panel = this.panelTarget && this.panelTarget.querySelector('[data-ugc-panel]');
      if (!panel) return;
      const cta = panel.querySelector('[data-ugc-cta]');
      if (cta) cta.setAttribute('aria-disabled', 'true');
      const idInput = panel.querySelector('input.product-variant-id');
      if (idInput) {
        idInput.value = '';
        idInput.disabled = true;
      }
    }

    /* Stars sind UGC-Inhalt (card.review_stars), kein Produkt-Rating → Client füllt
       den server-gerenderten Slot. Rundung wie card-product (0.3/0.7 → halber Stern). */
    fillStars(card) {
      const wrapper = this.panelTarget.querySelector('[data-ugc-stars]');
      if (!wrapper) return;

      const value = Number(card.review_stars);
      if (!Number.isFinite(value) || value <= 0) {
        wrapper.hidden = true;
        return;
      }

      const max = 5;
      const clamped = Math.min(value, max);
      const decimal = clamped % 1;
      let ratingDecimal = 0;
      if (decimal >= 0.3 && decimal <= 0.7) {
        ratingDecimal = 0.5;
      } else if (decimal > 0.7) {
        ratingDecimal = 1;
      }

      const star = wrapper.querySelector('.rating-star');
      if (star) {
        star.style.setProperty('--rating', String(Math.floor(clamped)));
        star.style.setProperty('--rating-max', String(max));
        star.style.setProperty('--rating-decimal', String(ratingDecimal));
      }

      const ratingEl = wrapper.querySelector('[data-ugc-stars-rating]');
      const panel = this.panelTarget.querySelector('[data-ugc-panel]');
      const labelTemplate = panel && panel.dataset.starsLabel;
      if (ratingEl && labelTemplate) {
        ratingEl.setAttribute(
          'aria-label',
          labelTemplate.replace('[rating_value]', String(clamped)).replace('[rating_max]', String(max))
        );
      }

      const valueEl = wrapper.querySelector('[data-ugc-stars-value]');
      if (valueEl) valueEl.textContent = String(clamped);

      wrapper.hidden = false;
    }

    setPanelLoading(loading) {
      if (!this.panelSlot) return;
      this.panelSlot.classList.toggle('is-loading', loading);
      const spinner = this.panelSlot.querySelector('.loading__spinner');
      if (spinner) spinner.classList.toggle('hidden', !loading);
    }

    clearPanel() {
      if (this.panelTarget) this.panelTarget.innerHTML = '';
      /* Stabile Preis-Live-Region mitleeren — sie lebt außerhalb des Panel-Targets und wird nur beim
         expliziten Variantenwechsel beschrieben (announcePrice). Ohne dieses Reset bliebe der zuletzt
         angesagte Preis beim Story-Wechsel / bei produktloser Story / beim Schließen im A11y-Tree stehen.
         Nur leeren (kein Preis-Write); loadPanels Erfolgs-Pfad ruft clearPanel nicht → Variantenwechsel-Ansage unberührt. */
      if (this.priceStatus) this.priceStatus.textContent = '';
      this.setPanelLoading(false);
    }
  }

  customElements.define('ugc-story-modal', UgcStoryModal);
})();
